import PropTypes from 'prop-types';
import { CheckInStatus } from '@eti/types';
import { Heading, Notice, Stack, Text } from '@etg/wings';
import { useProperty, useSiteContext, useTranslation } from '@eti/providers';
import { DangerousHtmlContent, Notification, variants } from '@eti/components';
import { useMemo } from 'react';
import { css } from '@eti/styles';
import { useOrderDetails } from '../../../../containers/OrderDetailsContext';
import { getCheckInStatusContent, getFormattedBounds } from './utils/utils';
import Bound from './Bound';

const descriptionStyles = css`
  display: inline;
`;

const PurchasedCheckIn = ({ checkInStatus, type }) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { language } = useSiteContext();
  const { bounds, travelers, oldOrderNumber } = useOrderDetails();

  const checkInTimeframeInHours = p('OrderDetails.CheckIn.Timeframe.Hours');
  const hasMissingDocs =
    checkInStatus &&
    !checkInStatus.errorCode &&
    checkInStatus.legs?.some((leg) =>
      leg.passengerLegs?.some((passenger) => passenger.status === 'SUSPENDED'),
    );

  const formattedBounds = useMemo(
    () => getFormattedBounds(bounds, checkInStatus, checkInTimeframeInHours, travelers),
    [bounds, checkInStatus, checkInTimeframeInHours, travelers],
  );

  const checkInContent = useMemo(
    () => getCheckInStatusContent(formattedBounds, type),
    [formattedBounds, type],
  );

  return (
    <Stack data-testid={`purchased-checkin-${type}`}>
      {checkInContent?.notification && (
        <Notification dataTestId="check-in-notification" variant="critical">
          {t(checkInContent.notification)}
        </Notification>
      )}
      <Stack spacing={8}>
        <Heading as="h2" level={3}>
          {t('OrderDetails.CheckIn.Purchased.Status.Title')}
        </Heading>
        {checkInContent && (
          <div>
            <strong>{t(checkInContent.title)} </strong>
            <DangerousHtmlContent
              className={descriptionStyles}
              content={t(checkInContent.description)}
            />
          </div>
        )}
        {checkInContent?.disclaimer && <div>{t(checkInContent.disclaimer)}</div>}
        {checkInContent?.extraInfo && <div>{t(checkInContent.extraInfo)}</div>}
        {checkInContent?.notice && <Notice variant="critical">{t(checkInContent?.notice)}</Notice>}
      </Stack>
      {hasMissingDocs && (
        <Notification
          dataTestId="purchased-checkin-warning-missing-doc-notification"
          title={t('CheckIn.MissingDocs.Notification.Title')}
          variant={variants.WARNING}
        >
          <Text as="div">{t('CheckIn.MissingDocs.Notification.Description')}</Text>
          <a
            href={`https://console.passnfly.com/checkin/activate/${oldOrderNumber}?language=${
              language?.code ?? 'en'
            }`}
            rel="noreferrer"
            target="_blank"
          >
            {t('CheckIn.MissingDocs.Notification.Link.Text')}
          </a>
        </Notification>
      )}

      <Stack role="list">
        {formattedBounds.map((bound, index) => (
          <Bound bound={bound} index={index} key={`checkin-bound-${bound.boundId}`} />
        ))}
      </Stack>
    </Stack>
  );
};

PurchasedCheckIn.propTypes = {
  checkInStatus: CheckInStatus,
  type: PropTypes.string,
};

export default PurchasedCheckIn;
